import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import React from 'react';

interface ReportItem {
  id?: string;
  name: string;
  category: string;
  rating: number;
  notes?: string;
  notApplicable?: boolean;
}

interface ReportRecord {
  id: string;
  period: string;
  dateRange: { start: string; end: string };
  submittedAt: string;
  averageRating: number;
  assessmentType: string;
  items: ReportItem[];
  sport?: string;
  notes?: string;
  coachId?: string;
}

interface Player {
  id: string;
  firstName?: string;
  lastName?: string;
  user?: {
    firstName?: string;
    lastName?: string;
  };
  academyName?: string;
}

// Cache for icon images
const iconImageCache: Map<number, string> = new Map();

// Pre-generate all icon images
const generateIconImages = async (): Promise<void> => {
  const ratings = [1, 2, 3, 4, 5];
  
  try {
    const { createRoot } = await import('react-dom/client');
    const {
      SentimentVeryDissatisfied: VeryBadIcon,
      SentimentDissatisfied: BadIcon,
      SentimentNeutral: NeutralIcon,
      SentimentSatisfied: GoodIcon,
      SentimentVerySatisfied: ExcellentIcon,
    } = await import('@mui/icons-material');

    // Generate each icon separately to avoid conflicts
    for (const rating of ratings) {
      const container = document.createElement('div');
      container.style.position = 'absolute';
      container.style.left = '-9999px';
      container.style.top = '-9999px';
      container.style.width = '48px';
      container.style.height = '48px';
      container.style.display = 'flex';
      container.style.alignItems = 'center';
      container.style.justifyContent = 'center';
      container.style.backgroundColor = 'white';
      document.body.appendChild(container);

      const IconComponent = rating <= 1 ? VeryBadIcon : 
                           rating <= 2 ? BadIcon : 
                           rating <= 3 ? NeutralIcon : 
                           rating <= 4 ? GoodIcon : 
                           ExcellentIcon;
      
      const iconColor = rating <= 1 ? '#f44336' : 
                       rating <= 2 ? '#ff9800' : 
                       rating <= 3 ? '#ffc107' : 
                       rating <= 4 ? '#4caf50' : '#2196f3';

      const root = createRoot(container);
      root.render(
        React.createElement(IconComponent, { 
          sx: { 
            fontSize: 40,
            color: iconColor,
            fontWeight: 'bold'
          }
        })
      );

      await new Promise(resolve => setTimeout(resolve, 150));

      const canvas = await html2canvas(container, {
        width: 48,
        height: 48,
        backgroundColor: 'white',
        scale: 3,
        logging: false,
        useCORS: true
      });
      
      iconImageCache.set(rating, canvas.toDataURL('image/png'));

      root.unmount();
      document.body.removeChild(container);
    }
  } catch (error) {
    console.error('Error generating icon images:', error);
  }
};

const getPeriodLabel = (period: string): string => {
  if (period.startsWith('month-')) {
    const monthIndex = parseInt(period.split('-')[1]) - 1;
    const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    return months[monthIndex] || period;
  }
  if (period.startsWith('quarter-')) {
    const quarter = period.split('-')[1];
    return `Q${quarter}`;
  }
  if (period === 'yearly') {
    return 'Yearly';
  }
  return period;
};

export const generatePerformanceReportPDF = async (report: ReportRecord, player: Player): Promise<void> => {
  if (!report.items || report.items.length === 0) {
    alert('No evaluation data available to generate report');
    return;
  }
  
  // Pre-generate all icon images
  await generateIconImages();
  
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 10;
  const footerHeight = 15;
  const headerHeight = 20;
  let yPosition = headerHeight;
  const rowHeight = 7;
  const tableStartX = margin;
  
  // Helper function to convert hex to RGB
  const hexToRgb = (hex: string): number[] => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? [
      parseInt(result[1], 16),
      parseInt(result[2], 16),
      parseInt(result[3], 16)
    ] : [0, 0, 0];
  };
  
  // Get category color
  const getCategoryColor = (category: string) => {
    const colors: { [key: string]: { bg: string; text: string } } = {
      'Footwork': { bg: '#e8f5e8', text: '#2e7d32' },
      'Grip and Rotation': { bg: '#fff8e1', text: '#f57f17' },
      'Forehand Shots': { bg: '#e3f2fd', text: '#1565c0' },
      'Backhand Shots': { bg: '#e1f5fe', text: '#0277bd' },
      'Fitness': { bg: '#f3e5f5', text: '#7b1fa2' },
      'Follow Instructions': { bg: '#e0f2f1', text: '#00695c' },
      'Interest': { bg: '#fce4ec', text: '#c2185b' },
      'Forehand Strokes': { bg: '#e3f2fd', text: '#1565c0' },
      'Backhand Strokes': { bg: '#e1f5fe', text: '#0277bd' },
      'Behavioural skills': { bg: '#fff3e0', text: '#e65100' },
    };
    return colors[category] || { bg: '#f5f5f5', text: '#666' };
  };
  
  // Get performance level
  const getPerformanceLevel = (rating: number) => {
    if (rating === 0) return { text: 'Not Rated', color: '#9e9e9e' };
    if (rating <= 1) return { text: 'Needs Improvement', color: '#f44336' };
    if (rating <= 2) return { text: 'Below Average', color: '#ff9800' };
    if (rating <= 3) return { text: 'Average', color: '#ffc107' };
    if (rating <= 4) return { text: 'Good', color: '#4caf50' };
    return { text: 'Excellent', color: '#2196f3' };
  };
  
  // Header with branding
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.text('Encycdata', margin, 8);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text('EncycGames', margin, 13);
  
  // Assessment title
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  // Handle both old ('quick'/'advanced') and new ('high-level'/'low-level') values
  const normalizedType = report.assessmentType === 'quick' || report.assessmentType === 'high-level' 
    ? 'high-level' 
    : report.assessmentType === 'advanced' || report.assessmentType === 'low-level'
    ? 'low-level'
    : report.assessmentType;
  const assessmentTitle = normalizedType === 'low-level' ? 'Low-level Assessment' : 'High-level Assessment';
  doc.text(assessmentTitle, pageWidth / 2, yPosition, { align: 'center' });
  yPosition += 8;
  
  // Header Information
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  const playerName = `${player?.firstName || player?.user?.firstName || ''} ${player?.lastName || player?.user?.lastName || ''}`.trim();
  const reportDate = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  const reportYear = new Date(report.dateRange.start).getFullYear();
  
  // Load coach name (if available from report data)
  const coachName = report.coachId ? 'Coach' : 'Self Evaluation';
  
  doc.text(`Player: ${playerName}`, margin, yPosition);
  doc.text(`Coach: ${coachName}`, pageWidth / 2, yPosition);
  yPosition += 5;
  doc.text(`Academy: ${player?.academyName || 'N/A'}`, margin, yPosition);
  doc.text(`Date: ${reportDate}`, pageWidth / 2, yPosition);
  yPosition += 5;
  doc.text(`Period: ${report.dateRange.start} to ${report.dateRange.end} (${getPeriodLabel(report.period)} ${reportYear})`, margin, yPosition);
  yPosition += 8;

  // Rating Scale Reference Information
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.text('Rating Scale Reference (For Information Only):', margin, yPosition);
  yPosition += 6;
  
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  
  // Calculate the maximum width of level names for alignment
  const levelNames = [
    'Beginner (1-2):',
    'Intermediate (2-3):',
    'Advanced (3-4):',
    'Master (4-5):'
  ];
  doc.setFont('helvetica', 'bold');
  const maxLevelWidth = Math.max(...levelNames.map(name => doc.getTextWidth(name)));
  const definitionStartX = margin + 8 + maxLevelWidth + 5;
  
  // Beginner (1-2)
  doc.setFillColor(244, 67, 54);
  doc.rect(margin, yPosition - 2, 3, 3, 'F');
  doc.setFont('helvetica', 'bold');
  doc.text('Beginner (1-2):', margin + 8, yPosition);
  doc.setFont('helvetica', 'normal');
  doc.text('Learning basic rules and shots', definitionStartX, yPosition);
  yPosition += 5;
  
  // Intermediate (2-3)
  doc.setFillColor(255, 152, 0);
  doc.rect(margin, yPosition - 2, 3, 3, 'F');
  doc.setFont('helvetica', 'bold');
  doc.text('Intermediate (2-3):', margin + 8, yPosition);
  doc.setFont('helvetica', 'normal');
  doc.text('Can perform upto 5 consistent strokes but lacking accuracy and perfection', definitionStartX, yPosition);
  yPosition += 5;
  
  // Advanced (3-4)
  doc.setFillColor(33, 150, 243);
  doc.rect(margin, yPosition - 2, 3, 3, 'F');
  doc.setFont('helvetica', 'bold');
  doc.text('Advanced (3-4):', margin + 8, yPosition);
  doc.setFont('helvetica', 'normal');
  doc.text('Can play 15 or more consistent strokes and do long rallies with different shots', definitionStartX, yPosition);
  yPosition += 5;
  
  // Master (4-5)
  doc.setFillColor(76, 175, 80);
  doc.rect(margin, yPosition - 2, 3, 3, 'F');
  doc.setFont('helvetica', 'bold');
  doc.text('Master (4-5):', margin + 8, yPosition);
  doc.setFont('helvetica', 'normal');
  doc.text('Aggressive, Accurate and consistently performing in varying conditions', definitionStartX, yPosition);
  yPosition += 8;
  
  // Table column widths
  const colWidths = {
    skill: (pageWidth - 2 * margin) * 0.38,
    notApplicable: (pageWidth - 2 * margin) * 0.10,
    rating: (pageWidth - 2 * margin) * 0.18,
    performance: (pageWidth - 2 * margin) * 0.27,
    smiley: (pageWidth - 2 * margin) * 0.07
  };
  
  const drawTableRow = (x: number, y: number, lineWidth: number = 0.2) => {
    doc.setLineWidth(lineWidth);
    let currentX = x;
    const lineY = y + rowHeight;
    const tableEndX = x + (pageWidth - 2 * margin);
    
    doc.line(currentX, y, currentX, lineY);
    currentX += colWidths.skill;
    doc.line(currentX, y, currentX, lineY);
    currentX += colWidths.notApplicable;
    doc.line(currentX, y, currentX, lineY);
    currentX += colWidths.rating;
    doc.line(currentX, y, currentX, lineY);
    currentX += colWidths.performance;
    doc.line(currentX, y, currentX, lineY);
    currentX += colWidths.smiley;
    doc.line(currentX, y, currentX, lineY);
    doc.line(x, lineY, tableEndX, lineY);
  };
  
  const drawTableHeader = (y: number) => {
    doc.setFillColor(245, 245, 245);
    doc.rect(tableStartX, y, pageWidth - 2 * margin, rowHeight, 'F');
    doc.setLineWidth(0.5);
    doc.line(tableStartX, y, tableStartX + (pageWidth - 2 * margin), y);
    drawTableRow(tableStartX, y, 0.3);
    
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    let textX = tableStartX;
    doc.text('Skill', textX + colWidths.skill / 2, y + 5, { align: 'center' });
    textX += colWidths.skill;
    doc.text('N/A', textX + colWidths.notApplicable / 2, y + 5, { align: 'center' });
    textX += colWidths.notApplicable;
    doc.text('Rating', textX + colWidths.rating / 2, y + 5, { align: 'center' });
    textX += colWidths.rating;
    doc.text('Performance', textX + colWidths.performance / 2, y + 5, { align: 'center' });
    textX += colWidths.performance;
    doc.text('Smiley', textX + colWidths.smiley / 2, y + 5, { align: 'center' });
  };
  
  const drawTableDataRow = (item: ReportItem, y: number, categoryColor?: { bg: number[], text: number[] }) => {
    drawTableRow(tableStartX, y, 0.2);
    
    if (categoryColor) {
      doc.setFillColor(categoryColor.bg[0], categoryColor.bg[1], categoryColor.bg[2]);
      doc.rect(tableStartX, y, pageWidth - 2 * margin, rowHeight, 'F');
      drawTableRow(tableStartX, y, 0.2);
    }
    
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    let textX = tableStartX + 2;
    
    const skillName = doc.splitTextToSize(item.name, colWidths.skill - 4);
    doc.text(skillName, textX, y + 5);
    
    // Not Applicable
    textX += colWidths.skill + 2;
    if (item.notApplicable) {
      doc.text('N/A', textX, y + 5);
    } else {
      doc.text('-', textX, y + 5);
    }
    
    // Rating - draw visual circles
    textX += colWidths.notApplicable + 2;
    if (!item.notApplicable && item.rating > 0) {
      const circleRadius = 2;
      const circleSpacing = 5;
      const circleY = y + 3.5;
      
      for (let i = 0; i < item.rating; i++) {
        const circleX = textX + (i * circleSpacing);
        doc.setFillColor(0, 0, 0);
        doc.circle(circleX, circleY, circleRadius, 'F');
      }
      
      for (let i = item.rating; i < 5; i++) {
        const circleX = textX + (i * circleSpacing);
        doc.setDrawColor(0, 0, 0);
        doc.setLineWidth(0.3);
        doc.circle(circleX, circleY, circleRadius, 'S');
      }
    } else if (item.notApplicable) {
      doc.text('N/A', textX, y + 5);
    } else {
      doc.text('-', textX, y + 5);
    }
    
    textX += colWidths.rating + 2;
    if (item.notApplicable) {
      doc.text('N/A', textX, y + 5);
    } else if (item.rating > 0) {
      const performance = getPerformanceLevel(item.rating);
      const perfColor = hexToRgb(performance.color);
      doc.setTextColor(perfColor[0], perfColor[1], perfColor[2]);
      const perfTextLines = doc.splitTextToSize(performance.text, colWidths.performance - 2);
      doc.text(perfTextLines, textX, y + 5);
      doc.setTextColor(0, 0, 0);
    }
    
    // Smiley - use Material-UI icon image from cache
    const smileyColumnStartX = tableStartX + colWidths.skill + colWidths.notApplicable + colWidths.rating + colWidths.performance;
    
    if (!item.notApplicable && item.rating > 0) {
      const iconImage = iconImageCache.get(item.rating);
      if (iconImage) {
        const availableWidth = colWidths.smiley - 2;
        const availableHeight = rowHeight - 2;
        const iconSize = Math.min(availableWidth, availableHeight, 5);
        
        const columnCenterX = smileyColumnStartX + (colWidths.smiley / 2);
        const iconX = columnCenterX - (iconSize / 2);
        const iconY = y + (rowHeight / 2) - (iconSize / 2);
        
        const iconRight = iconX + iconSize;
        const columnRight = smileyColumnStartX + colWidths.smiley;
        const iconBottom = iconY + iconSize;
        const rowBottom = y + rowHeight;
        
        if (iconX >= smileyColumnStartX && iconRight <= columnRight && iconY >= y && iconBottom <= rowBottom) {
          doc.addImage(iconImage, 'PNG', iconX, iconY, iconSize, iconSize);
        } else {
          const safeIconSize = Math.min(colWidths.smiley - 2, rowHeight - 2);
          const safeIconX = smileyColumnStartX + (colWidths.smiley / 2) - (safeIconSize / 2);
          const safeIconY = y + (rowHeight / 2) - (safeIconSize / 2);
          doc.addImage(iconImage, 'PNG', safeIconX, safeIconY, safeIconSize, safeIconSize);
        }
      } else {
        const getSmileyEmoji = (rating: number): string => {
          if (rating <= 1) return '😢';
          if (rating <= 2) return '😕';
          if (rating <= 3) return '😐';
          if (rating <= 4) return '😊';
          return '😄';
        };
        const smileyEmoji = getSmileyEmoji(item.rating);
        const columnCenterX = smileyColumnStartX + (colWidths.smiley / 2);
        doc.setFontSize(12);
        const textWidth = doc.getTextWidth(smileyEmoji);
        doc.text(smileyEmoji, columnCenterX - (textWidth / 2), y + 5);
        doc.setFontSize(8);
      }
    }
  };
  
  const drawSectionBorder = (x: number, y: number, height: number, lineWidth: number = 0.8) => {
    doc.setLineWidth(lineWidth);
    const endX = x + (pageWidth - 2 * margin);
    const endY = y + height;
    
    doc.line(x, y, endX, y);
    doc.line(endX, y, endX, endY);
    doc.line(endX, endY, x, endY);
    doc.line(x, endY, x, y);
  };

  const addFooter = (doc: jsPDF, pageWidth: number, pageHeight: number, footerHeight: number) => {
    const footerY = pageHeight - footerHeight;
    
    doc.setLineWidth(0.5);
    doc.line(10, footerY, pageWidth - 10, footerY);
    
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.text('Encycdata', 10, footerY + 5);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.text('EncycGames', 10, footerY + 10);
    
    const pageNum = doc.getCurrentPageInfo().pageNumber;
    const totalPages = doc.getNumberOfPages();
    doc.setFontSize(8);
    doc.text(`Page ${pageNum} of ${totalPages}`, pageWidth / 2, footerY + 7, { align: 'center' });
    
    const footerDate = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    doc.text(footerDate, pageWidth - 10, footerY + 7, { align: 'right' });
  };

  // Start table
  let tableStartY = yPosition;
  drawTableHeader(tableStartY);
  yPosition += rowHeight;
  
  // Group items by category if low-level
  if (normalizedType === 'low-level') {
    const groupedItems = report.items.reduce((acc: any, item: any) => {
      if (!acc[item.category]) {
        acc[item.category] = [];
      }
      acc[item.category].push(item);
      return acc;
    }, {} as { [key: string]: any[] });
    
    Object.entries(groupedItems).forEach(([category, items]) => {
      if (yPosition + rowHeight * 2 > pageHeight - footerHeight) {
        addFooter(doc, pageWidth, pageHeight, footerHeight);
        doc.addPage();
        yPosition = headerHeight;
        tableStartY = yPosition;
        drawTableHeader(tableStartY);
        yPosition += rowHeight;
      }

      yPosition += 2;
      const categoryStartY = yPosition;
      const categoryColor = getCategoryColor(category);
      const bgColor = hexToRgb(categoryColor.bg);
      const textColor = hexToRgb(categoryColor.text);
      
      doc.setFillColor(bgColor[0], bgColor[1], bgColor[2]);
      doc.rect(tableStartX, yPosition, pageWidth - 2 * margin, rowHeight, 'F');
      
      doc.setLineWidth(0.5);
      doc.line(tableStartX, yPosition, tableStartX + (pageWidth - 2 * margin), yPosition);
      doc.line(tableStartX, yPosition + rowHeight, tableStartX + (pageWidth - 2 * margin), yPosition + rowHeight);
      
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(textColor[0], textColor[1], textColor[2]);
      doc.text(category.toUpperCase(), pageWidth / 2, yPosition + 5, { align: 'center' });
      doc.setTextColor(0, 0, 0);
      yPosition += rowHeight;

      items.forEach((item: any) => {
        if (yPosition + rowHeight > pageHeight - footerHeight) {
          const sectionHeight = yPosition - categoryStartY;
          drawSectionBorder(tableStartX, categoryStartY, sectionHeight, 0.8);
          
          addFooter(doc, pageWidth, pageHeight, footerHeight);
          doc.addPage();
          yPosition = headerHeight;
          tableStartY = yPosition;
          drawTableHeader(tableStartY);
          yPosition += rowHeight;
          
          const newCategoryStartY = yPosition;
          yPosition += 2;
          doc.setFillColor(bgColor[0], bgColor[1], bgColor[2]);
          doc.rect(tableStartX, yPosition, pageWidth - 2 * margin, rowHeight, 'F');
          doc.setLineWidth(0.5);
          doc.line(tableStartX, yPosition, tableStartX + (pageWidth - 2 * margin), yPosition);
          doc.line(tableStartX, yPosition + rowHeight, tableStartX + (pageWidth - 2 * margin), yPosition + rowHeight);
          doc.setFontSize(10);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(textColor[0], textColor[1], textColor[2]);
          doc.text(category.toUpperCase(), pageWidth / 2, yPosition + 5, { align: 'center' });
          doc.setTextColor(0, 0, 0);
          yPosition += rowHeight;
        }

        drawTableDataRow(item, yPosition, { bg: bgColor, text: textColor });
        yPosition += rowHeight;
      });
      
      const ratedItems = items.filter((item: any) => !item.notApplicable && item.rating > 0);
      const averageScore = ratedItems.length > 0
        ? (ratedItems.reduce((sum: number, item: any) => sum + item.rating, 0) / ratedItems.length).toFixed(2)
        : 'N/A';
      
      if (yPosition + rowHeight * 2 > pageHeight - footerHeight) {
        const sectionHeight = yPosition - categoryStartY;
        drawSectionBorder(tableStartX, categoryStartY, sectionHeight, 0.8);
        
        addFooter(doc, pageWidth, pageHeight, footerHeight);
        doc.addPage();
        yPosition = headerHeight;
        tableStartY = yPosition;
        drawTableHeader(tableStartY);
        yPosition += rowHeight;
      }
      
      doc.setLineWidth(0.3);
      doc.line(tableStartX, yPosition, tableStartX + (pageWidth - 2 * margin), yPosition);
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.text(`Average Score: ${averageScore}`, tableStartX + 5, yPosition + 5);
      yPosition += rowHeight;
      
      const notesLines = 5;
      const notesLineHeight = 4;
      
      if (yPosition + notesLines * notesLineHeight > pageHeight - footerHeight) {
        const sectionHeight = yPosition - categoryStartY;
        drawSectionBorder(tableStartX, categoryStartY, sectionHeight, 0.8);
        
        addFooter(doc, pageWidth, pageHeight, footerHeight);
        doc.addPage();
        yPosition = headerHeight;
        tableStartY = yPosition;
        drawTableHeader(tableStartY);
        yPosition += rowHeight;
      }
      
      doc.setLineWidth(0.3);
      doc.rect(tableStartX, yPosition, pageWidth - 2 * margin, notesLines * notesLineHeight);
      doc.setFontSize(8);
      doc.setFont('helvetica', 'bold');
      doc.text('Notes/Comments:', tableStartX + 2, yPosition + 3);
      
      const notesText = '';
      const maxWidth = pageWidth - 2 * margin - 4;
      const words = notesText.split(' ');
      let currentLine = '';
      let lineY = yPosition + 6;
      let lineCount = 0;
      
      words.forEach((word) => {
        const testLine = currentLine + (currentLine ? ' ' : '') + word;
        const textWidth = doc.getTextWidth(testLine);
        if (textWidth > maxWidth && currentLine) {
          if (lineCount < notesLines) {
            doc.setFontSize(8);
            doc.setFont('helvetica', 'normal');
            doc.text(currentLine, tableStartX + 2, lineY);
            lineY += notesLineHeight;
            lineCount++;
          }
          currentLine = word;
        } else {
          currentLine = testLine;
        }
      });
      if (currentLine && lineCount < notesLines) {
        doc.setFontSize(8);
        doc.setFont('helvetica', 'normal');
        doc.text(currentLine, tableStartX + 2, lineY);
        lineCount++;
      }
      
      while (lineCount < notesLines) {
        lineY += notesLineHeight;
        lineCount++;
      }
      
      yPosition += notesLines * notesLineHeight;
      
      const sectionHeight = yPosition - categoryStartY;
      drawSectionBorder(tableStartX, categoryStartY, sectionHeight, 0.8);
    });
  } else {
    const quickSectionStartY = yPosition;
    report.items.forEach((item: any) => {
      if (yPosition + rowHeight > pageHeight - footerHeight) {
        const sectionHeight = yPosition - quickSectionStartY;
        if (sectionHeight > 0) {
          drawSectionBorder(tableStartX, quickSectionStartY, sectionHeight, 0.8);
        }
        
        addFooter(doc, pageWidth, pageHeight, footerHeight);
        doc.addPage();
        yPosition = headerHeight;
        tableStartY = yPosition;
        drawTableHeader(tableStartY);
        yPosition += rowHeight;
        
        const newQuickSectionStartY = yPosition;
      }

      drawTableDataRow(item, yPosition);
      yPosition += rowHeight;
    });
    
    const quickSectionHeight = yPosition - quickSectionStartY;
    if (quickSectionHeight > 0) {
      drawSectionBorder(tableStartX, quickSectionStartY, quickSectionHeight, 0.8);
    }
  }

  // Notes section
  if (yPosition + 30 > pageHeight - footerHeight) {
    addFooter(doc, pageWidth, pageHeight, footerHeight);
    doc.addPage();
    yPosition = headerHeight;
  } else {
    yPosition += 5;
  }
  
  doc.setLineWidth(0.5);
  doc.line(margin, yPosition, pageWidth - margin, yPosition);
  yPosition += 8;
  
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text('Notes:', margin, yPosition);
  yPosition += 8;
  
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  const notesText = report.notes && report.notes.trim() ? report.notes : '_________________________________________________________________\n_________________________________________________________________\n_________________________________________________________________';
  const notesLines = doc.splitTextToSize(notesText, pageWidth - 2 * margin);
  doc.text(notesLines, margin, yPosition);

  addFooter(doc, pageWidth, pageHeight, footerHeight);
  
  // Save PDF
  const fileName = `${normalizedType === 'low-level' ? 'Low-level' : 'High-level'}_Assessment_${playerName.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
  doc.save(fileName);
};













